/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch'
import React from 'react'
import deepForceUpdate from 'react-deep-force-update'
import ReactDOM from 'react-dom'
import queryString from 'query-string'
import _get from 'lodash/get'
import Axios from 'axios'
import { createPath } from 'history'
import App from './components/App'
import configureStore from './store/configureStore'
import history from './history'
import { updateMeta } from './DOMUtils'
import router from './router'
// import * as serviceWorker from './service-worker'

// import config from '@source/config'
// import Auth from '@api/auth'

// let inboxInterval;
// let count = window.App.inbox.unread;
// clearInterval(inboxInterval);

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss())
    return () => {
      removeCss.forEach(f => f())
    }
  },
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(window.App.state, { history }),
  storeSubscription: null,
  isMobile: window.App.isMobile,
}

// inboxInterval = setInterval(() => {
//   console.log(`client inbox interval ${count}`);
//   count++;
//   context.store.dispatch(setRuntimeVariable({ name: 'unread', value: count }));
// }, 1000);

const container = document.getElementById('app')
let currentLocation = history.location
let appInstance

let scrollPositionsHistory = {}
const newSubscribeEvent = async () => {
  const {
    runtime: { api, appId },
    user: { uid, token },
  } = context.store.getState()

  if (uid) {
    const newSubscriberStatus = await Axios.get(`${api}/subscriptions/new-subscriber`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        app_id: appId,
      },
      timeout: 5000,
    })
      .then(response => _get(response, 'data.data.status', 0))
      .catch(error => 0)

    if (newSubscriberStatus === 1) {
      if (window.fbq) {
        fbq('track', 'Subscribe', { value: '1.00' })
        const updateSubscriberResp = await Axios.patch(
          `${api}/subscriptions/new-subscriber`,
          {
            params: {
              app_id: appId,
            },
          },
          {
            timeout: 5000,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
          .then(response => _get(response, 'data.data', null))
          .catch(error => null)
      }
      gtag('js', new Date())
      gtag('config', 'AW-732544641')
    } else {
      // gtag('config', 'AW-732544641', { send_page_view: false })
    }
  }
}
// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  if (context.isMobile && currentLocation.pathname.indexOf('/watch') < 0) {
    scrollPositionsHistory[currentLocation.pathname] = {
      scrollTop: document.body.scrollTop,
    }
  } else if (
    document &&
    document.getElementsByClassName('children__container').length > 0 &&
    currentLocation.pathname.indexOf('/watch') < 0
  ) {
    scrollPositionsHistory[currentLocation.pathname] = {
      scrollTop: document.getElementsByClassName('children__container')[0].scrollTop,
    }
  } else {
    scrollPositionsHistory[currentLocation.pathname] = {
      scrollTop: 0,
    }
  }

  currentLocation = location

  const isInitialRender = !action
  try {
    context.pathname = location.pathname
    context.query = queryString.parse(location.search)

    const appParamsRoutes = _get(context.store.getState(), 'configParams.routes', [])
    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router(appParamsRoutes).resolve(context)

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return
    }

    newSubscribeEvent()

    if (route.redirect) {
      history.replace(route.redirect)
      return
    }

    const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render
    appInstance = renderReactApp(<App context={context}>{route.component}</App>, container, async () => {
      if (isInitialRender) {
        // Switch off the native scroll restoration behavior and handle it manually
        // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
        if (window.history && 'scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual'
        }

        const elem = document.getElementById('css')
        if (elem) elem.parentNode.removeChild(elem)
        return
      }

      document.title = route.title

      updateMeta('description', route.description)

      let scrollX = 0
      let scrollY = 0
      const pos = scrollPositionsHistory[location.pathname]
      // if (context.isMobile) {
      //   // console.log('masuk?', pos.scrollTop, document.body.scrollTop)
      //   setTimeout(() => {
      //     document.body.scrollTop = pos ? pos.scrollTop : 0
      //   }, 500)
      // } else if (document && document.getElementsByClassName('children__container').length > 0) {
      //   document.getElementsByClassName('children__container')[0].scrollTop = pos ? pos.scrollTop : 0
      // } else {
      //   document.body.scrollTop = 0
      // }

      if (document && document.getElementsByClassName('children__container').length > 0) {
        document.getElementsByClassName('children__container')[0].scrollTop = 0
      } else {
        document.body.scrollTop = 0
      }

      // Google Analytics tracking. Don't send 'pageview' event after
      // the initial rendering, as it was already sent
      if (window.ga) {
        window.ga('send', 'pageview', createPath(location))
      }
    })
  } catch (error) {
    if (__DEV__) {
      throw error
    }

    console.error(error)

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error')
      window.location.reload()
    }
  }

  window.debugError = context.store.getState().runtime.debugError
  window.debugStore = context.store.getState()
}
// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange)
onLocationChange(currentLocation)

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance)
    }

    onLocationChange(currentLocation)
  })
}

// if ('serviceWorker' in navigator) {
//   serviceWorker.register()
// }

if ('serviceWorker' in navigator) {
  // window.addEventListener('load', function() {
  //   navigator.serviceWorker.register('/OneSignalSDKWorker.js').then(
  //     function(registration) {
  //       // window.Notification.requestPermission();
  //       console.log('ServiceWorker registration successful with scope: ', registration.scope)
  //     },
  //     function(err) {
  //       console.log('ServiceWorker registration failed: ', err)
  //     }
  //   )
  // })
}
